import React, { useEffect, useState } from 'react';

import { auth, appCheck } from '../firebaseConfig'; // Adjust path to your firebase config

import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import LocalizedLink from './LocalizedLink';
import { signOut } from 'firebase/auth';
import { db } from '../firebaseConfig'; // Adjust path to your firebase config
import { doc, getDoc } from 'firebase/firestore';
import { getToken } from 'firebase/app-check';
import { cancelSubscription, resumeSubscription, syncSubscription } from '../services';
import { Brain, LogOut, User, CreditCard, RefreshCw } from 'lucide-react';
import { Password } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
interface UserData {
  firstName?: string;
  subscription_name?: string;
  hits?: number;
  email?:string;
  cancel_at_period_end?: boolean;
  payment_gateway?: string;
  active_status?: string;
  stripe_subscription_status?: string;
  pending_stripe_subscription_id?: string;
  last_topup_status?: string;
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useLocalizedNavigate();

  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/');
        return;
      }

      try {
        // Ensure App Check token is available before Firestore operations
        if (appCheck) {
          try {
            await getToken(appCheck);
            console.log("✅ App Check token verified for SettingsPage Firestore operations");
          } catch (appCheckError) {
            console.error("❌ App Check token verification failed in SettingsPage:", appCheckError);
            // Continue anyway in development, but log the error
            if (!import.meta.env.DEV) {
              throw appCheckError;
            }
          }
        }
        
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setUserData(docSnap.data() as UserData);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSyncSubscription = async () => {
    if (!auth.currentUser) return;
    
    setSyncing(true);
    try {
      await syncSubscription(auth.currentUser.uid);
      // Refresh user data after sync
      const docRef = doc(db, 'users', auth.currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUserData(docSnap.data() as UserData);
      }
    } catch (error) {
      console.error('Error syncing subscription:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!auth.currentUser) return;
    if (userData?.subscription_name === 'free_plan') return;
    if (userData?.cancel_at_period_end) return;

    const ok = window.confirm("Cancel at the end of the current billing period? You'll keep access until then.");
    if (!ok) return;

    setCancelling(true);
    try {
      await cancelSubscription();
      const docRef = doc(db, 'users', auth.currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUserData(docSnap.data() as UserData);
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    } finally {
      setCancelling(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!auth.currentUser) return;
    if (!userData?.cancel_at_period_end) return;
    if (userData?.payment_gateway !== 'stripe') return;

    const ok = window.confirm('Resume your subscription and keep it renewing normally?');
    if (!ok) return;

    setResuming(true);
    try {
      await resumeSubscription();
      const docRef = doc(db, 'users', auth.currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUserData(docSnap.data() as UserData);
      }
    } catch (error) {
      console.error('Error resuming subscription:', error);
    } finally {
      setResuming(false);
    }
  };

  const stripeSubscriptionStatus = userData?.stripe_subscription_status || userData?.active_status;
  const hasProcessingTopup = userData?.last_topup_status === 'processing';
  const hasProcessingSubscription =
    userData?.payment_gateway === 'stripe' &&
    Boolean(userData?.pending_stripe_subscription_id) &&
    stripeSubscriptionStatus === 'incomplete';
  const hasPendingStripePayment = hasProcessingTopup || hasProcessingSubscription;

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="sl-settings-shell min-h-screen text-white p-8">
      <div className="sl-settings-card max-w-2xl mx-auto bg-white rounded-lg shadow-xl p-6">
        <h1 className="sl-settings-heading text-2xl font-bold mb-6 text-black">{t('settings.user_settings')}</h1>

        {/* User Details */}
        <div className="">
          <section className="sl-settings-section bg-white text-black p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <User className="sl-settings-icon w-6 h-6 text-black" />
              <h2 className="text-lg font-semibold">{t('settings.profile')}</h2>
            </div>
            <div className="sl-settings-copy space-y-2 text-black">
              <p>{t('settings.name')}: {userData?.firstName || userData?.email}</p>
              {!userData?.firstName && userData?.email && (
                <p className="text-xs text-gray-500 -mt-1">{t('settings.name_fallback_email_note')}</p>
              )}
              <p>{t('settings.email')}: {userData?.email || 'N/A'}</p>
            </div>
          </section>

          <section className="sl-settings-section bg-white p-4 rounded-lg">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <Brain className="sl-settings-icon w-6 h-6 text-black" />
                  <h2 className="sl-settings-copy text-black font-semibold">{t('settings.memory')}</h2>
                </div>
                <p className="sl-settings-copy text-sm text-gray-600">
                  {t('settings.memory_description')}
                </p>
              </div>
              <LocalizedLink
                to="/settings/memory"
                className="inline-flex shrink-0 items-center justify-center rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
              >
                {t('settings.manage_memory')}
              </LocalizedLink>
            </div>
          </section>

          {/* Subscription Details */}
	          <section className="sl-settings-section bg-white p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="sl-settings-icon w-6 h-6 text-black" />
              <h2 className="sl-settings-copy text-black font-semibold">{t('settings.subscription')}</h2>
              {hasPendingStripePayment && (
                <span
                  title={t('settings.payment_pending_title', { defaultValue: 'Payment in progress' })}
                  className="inline-flex h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.18)]"
                />
              )}
            </div>
            <div className="sl-settings-copy space-y-2 text-black">
              {hasPendingStripePayment && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
                  <p className="text-sm font-semibold">
                    {hasProcessingTopup
                      ? t('settings.topup_payment_processing_title', { defaultValue: 'Top-up payment in progress' })
                      : t('settings.subscription_payment_processing_title', {
                          defaultValue: 'Subscription payment in progress',
                        })}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed">
                    {hasProcessingTopup
                      ? t('settings.topup_payment_processing_body', {
                          defaultValue:
                            'Your credits will be added automatically once Stripe confirms the bank payment. ACH payments usually take 3-4 business days.',
                        })
                      : t('settings.subscription_payment_processing_body', {
                          defaultValue:
                            'Your plan will activate automatically once Stripe confirms the bank payment. ACH payments usually take 3-4 business days.',
                        })}
                  </p>
                </div>
              )}
              <p>{t('settings.plan')}: {userData?.subscription_name === 'free_plan' ? t('settings.free_plan') : userData?.subscription_name || t('settings.free')}</p>
              <p>{t('settings.tokens')}: {userData?.hits || 0}</p>
              {userData?.cancel_at_period_end && (
                <div className="mt-2">
                  <p>Cancellation scheduled.</p>
                  <p>You'll keep access until the end of your current billing period.</p>
                  {userData?.payment_gateway !== 'stripe' && (
                    <>
                      <p>If you change your mind, you'll need to subscribe again.</p>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="mt-2 px-4 py-2 rounded bg-gray-100 text-black hover:bg-gray-200 flex items-center gap-2 transition-colors"
                      >
                        Subscribe again
                      </button>
                    </>
                  )}
                </div>
              )}
              <LocalizedLink style={{width:"max-content"}} to="/reset-password" className="mt-2 px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                      <span className="flex items-center">
                        <Password className="w-4 h-4 mr-2" />
                        {t('settings.reset_password')}
                      </span>
                    </LocalizedLink>
              <button
                onClick={handleSyncSubscription}
                disabled={syncing}
                className={`mt-2 px-4 py-2 rounded bg-gray-100 text-black hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? t('settings.syncing') : t('settings.sync_subscription')}
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelling || userData?.subscription_name === 'free_plan' || Boolean(userData?.cancel_at_period_end)}
                className="mt-2 px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {cancelling ? 'Cancelling…' : userData?.cancel_at_period_end ? 'Cancellation scheduled' : 'Cancel subscription'}
              </button>
              {userData?.cancel_at_period_end && userData?.payment_gateway === 'stripe' && (
                <button
                  onClick={handleResumeSubscription}
                  disabled={resuming}
                  className="mt-2 px-4 py-2 rounded bg-gray-100 text-black hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {resuming ? 'Resuming…' : 'Resume subscription'}
                </button>
              )}
            </div>
	          </section>

	          {/* Logout */}
          <section>
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-200 rounded flex items-center justify-center gap-2 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {t('settings.logout')}
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
